import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlane,
  faUser,
  faCircleInfo,
  faBolt,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import { config } from "../../config";
import { useNavigate, useLocation } from "react-router-dom";
import ProgressBar from "../../components/flights/ProgressBar";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import FlightDetailsModal from "../../components/flights/FlightDetailsModal";
import { format, parseISO } from "date-fns";
import {
  calculateFinalPrice,
  formatPrice,
  calculateMarkup,
} from "../../utils/pricing";

const formatTitle = (title) => {
  const titles = {
    mr: "Mr",
    mrs: "Mrs",
    ms: "Ms",
    miss: "Miss",
    dr: "Dr",
  };
  return titles[title.toLowerCase()] || title;
};

function BookingSummary() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState("Credit Card");
  const [errors] = useState({});
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [isFlightModalOpen, setIsFlightModalOpen] = useState(false);
  const stripe = useStripe();
  const elements = useElements();

  // Get Data
  let bookingData;
  try {
    const rawData = searchParams.get("data");
    bookingData = JSON.parse(decodeURIComponent(rawData));
    console.log("Data Received from BookingDetail:", bookingData);
  } catch (error) {
    console.error("Error parsing booking data:", error);
  }

  // Define Key Variables
  const flight = bookingData.selectedOffer;
  const passengers = bookingData.passengers;
  // const price = bookingData?.selectedOffer?.data?.total_amount || "0.00";
  const currency = bookingData?.selectedOffer?.data?.total_currency || "GBP";
  const baseAmount = bookingData?.selectedOffer?.data?.base_amount || "0.00";
  // const taxAmount = bookingData?.selectedOffer?.data?.tax_amount || "0.00";

  // Create Payment Intent
  const createPaymentIntent = async () => {
    try {
      const finalPrice = calculateFinalPrice(flight.data.total_amount);

      const payload = {
        amount: Math.round(finalPrice * 100), // Convert to cents/pence
        currency: flight.data.total_currency,
      };
      console.log("📤 Creating Payment Intent Request:", payload);

      const response = await fetch(
        `${config.backendServer}/api/create-payment-intent`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();
      console.log(
        `📥 Payment Intent Response (Status: ${response.status}):`,
        data
      );

      if (data.error) {
        throw new Error(data.error);
      }
      if (!data.clientSecret) {
        throw new Error("No client secret received from server");
      }

      return data.clientSecret;
    } catch (err) {
      console.error("❌ Create Payment Intent Error:", err);
      throw err;
    }
  };

  // Book
  const handleSubmit = async (event) => {
    if (event) event.preventDefault();

    const duffelAmount = parseFloat(flight.data.total_amount);
    const stripeAmount = calculateFinalPrice(flight.data.total_amount);
    const profit = stripeAmount - duffelAmount;

    console.log("💰 Price Breakdown:", {
      duffelPrice: `${flight.data.total_currency} ${duffelAmount.toFixed(2)}`,
      customerPrice: `${flight.data.total_currency} ${stripeAmount.toFixed(2)}`,
      profit: `${flight.data.total_currency} ${profit.toFixed(2)}`,
      markup:
        config.pricing.markupType === "percentage"
          ? `${config.pricing.markupValue}%`
          : `${config.pricing.markupValue} ${flight.data.total_currency}`,
    });

    if (isProcessingPayment) return;
    setIsProcessingPayment(true);
    setPaymentError(null);

    try {
      if (!stripe || !elements) {
        throw new Error("Stripe has not been initialized");
      }

      console.log("🏁 Starting payment process...");
      const clientSecret = await createPaymentIntent();

      const cardElement = elements.getElement(CardElement);
      const { error: stripeError } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: `${passengers[0].firstName} ${passengers[0].lastName}`,
              email: passengers[0].email,
              phone: passengers[0].phone,
            },
          },
        }
      );

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      // Format phone number to E.164 format
      const formatPhoneNumber = (phone) => {
        // Remove all non-digit characters except leading +
        let cleaned = phone.replace(/[^\d+]/g, "");

        // Ensure it starts with + and has at least 10 digits
        if (!cleaned.startsWith("+")) {
          cleaned = "+" + cleaned;
        }

        // Ensure minimum length (country code + number)
        if (cleaned.length < 10) {
          return null; // Return null for invalid numbers
        }

        return cleaned;
      };

      // First, create a list of infants that need to be assigned
      const infantsToAssign = passengers
        .filter((p) => p.type === "infant_without_seat")
        .map((infant) => infant.id);

      // Then format the passengers
      const formattedPassengers = passengers.map((passenger, index) => {
        // Get the adult's phone number (first valid phone number in the list)
        const adultPhoneNumber = passengers.find(
          (p) => p.type === "adult" && formatPhoneNumber(p.phone)
        )?.phone;

        // Use the adult's phone number as fallback
        const phoneNumber =
          formatPhoneNumber(passenger.phone) ||
          formatPhoneNumber(adultPhoneNumber) ||
          "+447700900123"; // Final fallback

        const formattedPassenger = {
          type: passenger.type || "adult",
          title: passenger.title,
          given_name: passenger.firstName,
          family_name: passenger.lastName,
          phone_number: phoneNumber,
          born_on: passenger.dob ? passenger.dob.split("T")[0] : undefined,
          gender: passenger.gender === "male" ? "m" : "f",
          id: passenger.id,
          email: passenger.email || `placeholder${index + 1}@example.com`,
        };

        // If this is an adult and there are infants to assign, link them
        if (passenger.type === "adult" && infantsToAssign.length > 0) {
          formattedPassenger.infant_passenger_id = infantsToAssign.shift();
        }

        return formattedPassenger;
      });

      const requestBody = {
        selected_offers: [flight.data.id],
        passengers: formattedPassengers,
        type: "instant",
        payments: [
          {
            type: "balance",
            currency: flight.data.total_currency,
            amount: flight.data.total_amount, // Make sure this matches exactly
          },
        ],
      };

      // Create the order with Duffel
      console.log("📦 Creating order with data:", {
        offerId: flight.data.id,
        formattedPassengers,
        requestBody,
      });

      const response = await fetch(
        `${config.backendServer}/api/flights/orders`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      const orderData = await response.json();
      console.log("📥 Order Response:", orderData);

      if (!response.ok) {
        const errorMessage =
          orderData.errors?.[0]?.message ||
          orderData.error ||
          "Failed to create order";
        throw new Error(errorMessage);
      }

      // Send confirmation email
      console.log("📧 Sending confirmation email...");
      const finalAmount = calculateFinalPrice(flight.data.total_amount);
      const emailResponse = await fetch(
        `${config.backendServer}/api/booking-confirmation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            booking: flight,
            finalPaidAmount: finalAmount,
            currency: flight.data.total_currency,
            email: passengers[0].email,
            customerName: `${passengers[0].firstName} ${passengers[0].lastName}`,
            bookingRef: orderData.data.booking_reference,
            flightDetails: {
              origin: orderData.data.slices[0].origin.city_name,
              destination: orderData.data.slices[0].destination.city_name,
              outbound_date:
                orderData.data.slices[0].segments[0].departing_at.split("T")[0],
              outbound_time: orderData.data.slices[0].segments[0].departing_at
                .split("T")[1]
                .slice(0, 5),
              ...(orderData.data.slices[1] && {
                return_date:
                  orderData.data.slices[1].segments[0].departing_at.split(
                    "T"
                  )[0],
                return_time: orderData.data.slices[1].segments[0].departing_at
                  .split("T")[1]
                  .slice(0, 5),
              }),
            },
            payment: {
              total: orderData.data.total_amount,
              method: "Credit Card",
              date: new Date().toISOString().split("T")[0],
            },
          }),
        }
      );

      if (!emailResponse.ok) {
        console.error("Failed to send confirmation email");
      } else {
        console.log("✅ Confirmation email sent successfully!");
      }

      // Navigate to confirmation page
      navigate("/flights/confirmation", {
        state: {
          order: orderData,
          fromBookingSummary: true,
        },
      });
    } catch (error) {
      console.error("❌ Payment/Booking Error:", error);
      setPaymentError(error.message);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Render Payment Form
  const renderPaymentForm = () => {
    if (selectedPaymentMethod === "Credit Card") {
      return (
        <div className="mt-6">
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Card Details
                </h3>
                <div className="flex items-center space-x-2">
                  <img src="/visa.svg" alt="Visa" className="h-4" />
                  <img src="/mastercard.svg" alt="Mastercard" className="h-6" />
                  <img src="/amex.svg" alt="American Express" className="h-6" />
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: "16px",
                        fontFamily: '"Inter", sans-serif',
                        color: "#424770",
                        "::placeholder": {
                          color: "#aab7c4",
                        },
                        padding: "10px 0",
                      },
                      invalid: {
                        color: "#dc2626",
                        iconColor: "#dc2626",
                      },
                    },
                    hidePostalCode: true,
                  }}
                />
              </div>
            </div>

            {paymentError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 text-red-600 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="text-red-600">{paymentError}</span>
                </div>
              </div>
            )}

            <div className="mt-6">
              <div className="mb-4 flex justify-between items-center text-sm text-gray-600">
                <span>Amount to pay:</span>
                <span className="text-lg font-semibold text-gray-900">
                  {formatPrice(
                    calculateFinalPrice(flight.data.total_amount),
                    flight.data.total_currency
                  )}
                </span>
              </div>

              <button
                onClick={handleSubmit}
                disabled={isProcessingPayment || !stripe}
                className={`
                  w-full px-6 py-4 
                  ${
                    isProcessingPayment
                      ? "bg-purple-300"
                      : config.primaryColorBg
                  } 
                  text-gray-800 rounded-lg 
                  hover:bg-purple-700 hover:text-white
                  transition-colors duration-200
                  disabled:opacity-50 disabled:cursor-not-allowed
                  flex items-center justify-center space-x-2
                `}
              >
                {isProcessingPayment ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-gray-800"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span>Processing payment...</span>
                  </>
                ) : (
                  <>
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                    <span>Pay securely</span>
                  </>
                )}
              </button>

              <p className="mt-4 text-center text-sm text-gray-500">
                Your payment is secured by Stripe 🔒
              </p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Payment Methods
  const paymentMethods = [
    {
      name: "Credit Card",
      description: "Pay with your credit card",
      logo: "/credit-card-logo.png",
    },
  ];

  const calculateTotalFeesAndTaxes = () => {
    if (!bookingData?.selectedOffer?.data) return "0.00";

    const originalTax = parseFloat(
      bookingData.selectedOffer.data.tax_amount || 0
    );
    const markupAmount = calculateMarkup(
      bookingData.selectedOffer.data.total_amount
    );
    const totalFeesAndTaxes = originalTax + markupAmount;

    return totalFeesAndTaxes.toFixed(2);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container pb-4 mx-auto px-4 md:px-40 lg:px-40">
        <div className="flex justify-between py-6 items-center border-b">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              {flight.data.slices[0].origin.iata_code}{" "}
              <FontAwesomeIcon icon={faPlane} size="sm" />{" "}
              {flight.data.slices[0].destination.iata_code}
            </h1>
            <p className="text-gray-600">
              {format(
                parseISO(flight.data.slices[0].segments[0].departing_at),
                "EEE, d MMM '"
              )}
              {flight.data.slices[1] && (
                <>
                  {" • "}
                  {format(
                    parseISO(flight.data.slices[1].segments[0].departing_at),
                    "EEE, d MMM '"
                  )}
                </>
              )}
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold">
              {formatPrice(
                calculateFinalPrice(flight.data.total_amount),
                flight.data.total_currency
              )}
            </p>
            <p className="text-gray-600">Total price</p>
          </div>
        </div>
        <ProgressBar currentStep={2} />

        {/* Flight Summary */}
        <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
              <FontAwesomeIcon
                icon={faPlane}
                className={`${config.primaryColorText}`}
              />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Your flights</h2>
            </div>
          </div>

          {/* Flight Details */}
          <div
            className="space-y-4 cursor-pointer"
            onClick={() => setIsFlightModalOpen(true)}
          >
            <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
              <button
                className="w-full flex justify-between items-center mb-6"
                onClick={() => setIsFlightModalOpen(true)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center">
                    <img
                      src={flight.data.owner.logo_symbol_url}
                      alt={flight.data.owner.name}
                      className="w-10 h-10"
                    />
                  </div>
                  <div className="text-left">
                    <h2 className=" font-semibold">{flight.data.owner.name}</h2>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-end gap-0 flex-col">
                    <p className="text-lg font-bold">
                      {formatPrice(
                        calculateFinalPrice(flight.data.total_amount),
                        flight.data.total_currency
                      )}
                    </p>
                    <p className="text-xs text-gray-500">Total Price</p>
                  </div>
                </div>
              </button>

              {/* Outbound Flight */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex flex-col items-start">
                  <span className="text-lg font-semibold">
                    {format(
                      parseISO(flight.data.slices[0].segments[0].departing_at),
                      "HH:mm"
                    )}
                  </span>
                  <span className=" text-gray-600">
                    {flight.data.slices[0].origin.iata_code}
                  </span>
                </div>
                <div className="flex-1 px-8">
                  <div className="flex flex-col items-center">
                    <span className="text-gray-500 text-xs">
                      {flight.data.slices[0].segments.length === 1
                        ? "Direct"
                        : `${flight.data.slices[0].segments.length - 1} stop${
                            flight.data.slices[0].segments.length - 1 > 1
                              ? "s"
                              : ""
                          }`}
                    </span>
                    <div className="w-full flex items-center gap-2">
                      <div className="h-[2px] flex-1 bg-gray-300"></div>
                      <FontAwesomeIcon
                        icon={faPlane}
                        className="text-gray-400"
                      />
                      <div className="h-[2px] flex-1 bg-gray-300"></div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end">
                  <span className="text-lg font-semibold">
                    {format(
                      parseISO(flight.data.slices[0].segments[0].arriving_at),
                      "HH:mm"
                    )}
                  </span>
                  <span className=" text-gray-600">
                    {flight.data.slices[0].destination.iata_code}
                  </span>
                </div>
              </div>

              {/* Return Flight */}
              {flight.data.slices[1] && (
                <div className="flex items-center justify-between">
                  <div className="flex flex-col items-start">
                    <span className="text-lg font-semibold">
                      {format(
                        parseISO(
                          flight.data.slices[1].segments[0].departing_at
                        ),
                        "HH:mm"
                      )}
                    </span>
                    <span className=" text-gray-600">
                      {flight.data.slices[1].origin.iata_code}
                    </span>
                  </div>
                  <div className="flex-1 px-8">
                    <div className="flex flex-col items-center">
                      <span className="text-gray-500 text-xs">
                        {flight.data.slices[1].segments.length === 1
                          ? "Direct"
                          : `${flight.data.slices[1].segments.length - 1} stop${
                              flight.data.slices[1].segments.length - 1 > 1
                                ? "s"
                                : ""
                            }`}
                      </span>
                      <div className="w-full flex items-center gap-2">
                        <div className="h-[2px] flex-1 bg-gray-300"></div>
                        <FontAwesomeIcon
                          icon={faPlane}
                          className="text-gray-400"
                        />
                        <div className="h-[2px] flex-1 bg-gray-300"></div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-lg font-semibold">
                      {format(
                        parseISO(flight.data.slices[1].segments[0].arriving_at),
                        "HH:mm"
                      )}
                    </span>
                    <span className=" text-gray-600">
                      {flight.data.slices[1].destination.iata_code}
                    </span>
                  </div>
                </div>
              )}

              <div className="mt-6 text-sm flex justify-end">
                <button
                  className="bg-gray-100 text-gray-700 px-6 py-2 rounded-full flex items-center gap-2"
                  onClick={() => setIsFlightModalOpen(true)}
                >
                  Trip Details
                  <FontAwesomeIcon icon={faCopy} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Flight Details Modal */}
        <FlightDetailsModal
          isOpen={isFlightModalOpen}
          onClose={() => setIsFlightModalOpen(false)}
          selectedOffer={bookingData?.selectedOffer}
        />

        {/* Passengers */}
        <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
              <FontAwesomeIcon
                icon={faUser}
                className={`${config.primaryColorText}`}
              />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Passengers</h2>
            </div>
          </div>

          <div className="space-y-4">
            {passengers.map((passenger, index) => (
              <div
                key={index}
                className="flex items-center gap-4 border p-2 rounded-lg"
              >
                <div className="bg-coral-100 rounded-full w-12 h-12 flex items-center justify-center text-xl font-semibold text-coral-600">
                  {passenger.firstName[0]}
                </div>
                <div>
                  <p className="font-semibold">
                    {formatTitle(passenger.title)}. {passenger.firstName}{" "}
                    {passenger.lastName}
                  </p>
                  {index === 0 && (
                    <>
                      <p className="text-gray-600">{passenger.email}</p>
                      <p className="text-gray-600">{passenger.phone}</p>
                    </>
                  )}
                  <p className="text-gray-600 capitalize">
                    {passenger.type?.replace(/_/g, " ")}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Price Breakdown */}
        <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
              <FontAwesomeIcon
                icon={faCircleInfo}
                className={`${config.primaryColorText}`}
              />
            </div>
            <h2 className="text-lg font-semibold">Price breakdown</h2>
          </div>

          <div className="space-y-4">
            {/* Passenger Count Summary */}
            <div className="pb-4 border-b border-gray-200">
              <h3 className="font-medium mb-2">Passengers</h3>
              {bookingData?.selectedOffer?.data?.passengers?.length > 0 && (
                <div className="text-gray-600">
                  {Object.entries(
                    bookingData.selectedOffer.data.passengers.reduce(
                      (acc, passenger) => {
                        acc[passenger.type] = (acc[passenger.type] || 0) + 1;
                        return acc;
                      },
                      {}
                    )
                  ).map(([type, count]) => (
                    <div key={type} className="flex justify-between">
                      <span className="capitalize">
                        {type.replace(/_/g, " ")}
                      </span>
                      <span>x{count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Base Fare */}
            <div className="pb-4 border-b border-gray-200">
              <div className="flex justify-between">
                <span>Base fare</span>
                <span>£{baseAmount}</span>
              </div>
            </div>

            {/* Taxes and Fees */}
            <div className="pb-4 border-b border-gray-200">
              <div className="flex justify-between">
                <span>Taxes and fees</span>
                <span>
                  {formatPrice(
                    calculateTotalFeesAndTaxes(),
                    bookingData?.selectedOffer?.data?.total_currency || "GBP"
                  )}
                </span>
              </div>
            </div>

            {/* Total */}
            <div className="flex justify-between font-bold text-lg pt-2">
              <span>Total</span>
              <span>
                {formatPrice(
                  calculateFinalPrice(flight.data.total_amount),
                  flight.data.total_currency
                )}
              </span>
            </div>

            {/* Additional Info */}
            <div className="mt-4 text-sm text-gray-500">
              <p>* All prices are in {currency}</p>
              {bookingData?.selectedOffer?.data?.total_emissions_kg && (
                <p className="mt-2">
                  Estimated CO₂ emissions:{" "}
                  {bookingData.selectedOffer.data.total_emissions_kg}
                  kg
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Payment Selection */}
        <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faBolt}
                  className={`${config.primaryColorText}`}
                />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Payment</h2>
              </div>
            </div>
          </div>

          {/* Payment Methods Selection */}
          <div className="border border-purple-200 rounded-lg overflow-hidden mb-4">
            <div className="p-4 bg-gray-50">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon
                  icon={faBolt}
                  className={`${config.primaryColorText}`}
                />
                <span className="font-semibold">Select Payment Method</span>
              </div>
            </div>

            <div className="divide-y">
              {paymentMethods.map((method, index) => (
                <label
                  key={index}
                  className="flex items-center p-4 cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="radio"
                    name="payment"
                    className="form-radio text-purple-600"
                    checked={selectedPaymentMethod === method.name}
                    onChange={() => setSelectedPaymentMethod(method.name)}
                    defaultChecked={method.name === "Credit Card"}
                  />
                  <div className="ml-4">
                    <p className="font-semibold">{method.name}</p>
                    <p className="text-gray-600 text-sm">
                      {method.description}
                    </p>
                  </div>
                </label>
              ))}
              {renderPaymentForm()}

              {errors.payment && (
                <div className="mt-2 text-red-600">{errors.payment}</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BookingSummary;
